import React, { useContext } from 'react'
import { withRouter, RouteComponentProps } from 'react-router'
import { useKey } from 'react-use'

// Constants
import { routes } from 'constants/routes'
import { SKETCH_WEBSITE } from 'constants/sketchWebsite'

// Utils
import { keyWithModifier } from 'utils/keyboardEvents'

// Icons
import { ReactComponent as KeyIcon } from 'icons/key-8.svg'
import { ReactComponent as PersonIcon } from 'icons/person-20.svg'

// Components
import Link from 'components/Link'
import { useDropdownState } from 'components/Dropdown'
import Avatar from 'components/Avatar'
import { NavbarLinkWrapper } from 'components/Navbar'
import Caption from 'components/Caption'
import { Text } from 'components/Box'
import ModalContext from 'containers/ModalManager/ModalContext'
import { KeyboardShortcutsModal } from 'containers/modals/KeyboardShortcutsModal'

// Hooks
import useUserProfile from 'modules/user/useUserProfile'
import useSignOut from 'modules/user/useSignOut'

// Styles
import {
  IconWrapper,
  DropdownTrigger,
  UserDropdownWrapper,
  DropdownHeader,
  MenuSeparator,
  UserDropdownItem,
  Dropdown,
  PersonIconWrapper,
} from './UserDropdown.styles'

// Types
import { UserFragment } from 'gqlTypes'

interface UserDropdownProps extends RouteComponentProps {
  className?: string
  user?: UserFragment | null
}

const UserDropdown: React.FC<UserDropdownProps> = props => {
  const signOut = useSignOut()
  const { className, location, user } = props

  const isSuperadmin = !!user?.isSuperadmin

  const dropdown = useDropdownState({
    onToggle: () => {},
  })

  const {
    isModalOpen,
    showModal,
    hideModal,
    component: ModalComponent,
  } = useContext(ModalContext)

  // Keyboard shortcut (⌘ + /) to open keyboard shortcuts modal
  useKey(keyWithModifier('/'), () => {
    if (isModalOpen && ModalComponent === KeyboardShortcutsModal) {
      hideModal()
    } else {
      showModal(KeyboardShortcutsModal)
    }
  })

  if (user) {
    const { avatar, email, name } = user

    return (
      <div>
        {/* 👆 This Div exists to prevent the dropdown of creating another element and shifting the avatar left */}
        <UserDropdownWrapper
          {...dropdown}
          onClickOutside={dropdown.hide}
          className={className}
          toggle={
            <DropdownTrigger data-testid="user-avatar">
              <Avatar src={avatar!.small!} name={name!} size="28px" />
              {isSuperadmin && (
                <IconWrapper data-testid="super-admin-badge">
                  <KeyIcon width={16} />
                </IconWrapper>
              )}
            </DropdownTrigger>
          }
          placement="bottom-end"
          spacing="2px"
          minWidth="200px"
          maxWidth="200px"
          contentStyle={{
            marginTop: '4px',
          }}>
          <DropdownHeader>
            <Text fontSize="14px" fontWeight={500} m={0} overflow="ellipsis">
              {name}
            </Text>
            <Caption m={0} overflow="ellipsis" lineHeight="1">
              {email}
            </Caption>
          </DropdownHeader>

          <MenuSeparator />

          <div onClick={() => dropdown.hide()}>
            <UserDropdownItem
              target="_blank"
              href={`${SKETCH_WEBSITE}/downloads/mac`}
              rel="noopener noreferrer">
              Download the Mac App
            </UserDropdownItem>

            <Dropdown.Item
              onClick={() => {
                showModal(KeyboardShortcutsModal)
              }}>
              Keyboard Shortcuts
            </Dropdown.Item>

            <MenuSeparator />

            <Dropdown.Item onClick={signOut}>Sign Out</Dropdown.Item>
          </div>
        </UserDropdownWrapper>
      </div>
    )
  } else {
    return (
      <Link
        to={{ pathname: routes.SIGN_IN.create({}), state: { from: location } }}>
        <NavbarLinkWrapper>
          <PersonIconWrapper>
            <PersonIcon aria-hidden={true} height={18} width={18} />
          </PersonIconWrapper>
        </NavbarLinkWrapper>
        <span className="sr-only">Sign out</span>
      </Link>
    )
  }
}

const EnhancedUserDropdown = withRouter(UserDropdown)

const ConnectedUserDropdown: React.FC = () => {
  const { data } = useUserProfile()

  return <EnhancedUserDropdown user={data?.me} />
}

export { ConnectedUserDropdown }
export default EnhancedUserDropdown
